import { useMemo } from "react";
import {
  useCalcTaskRunningList,
  useStockTaskRunningList,
  useSyncTaskRunningList,
} from "hooks";
import moment from "moment";
import { TaskStatus, TaskType } from "types";

/**
 * Ab diesem Alter gilt eine noch auf "Running" stehende Zeile als verwaist: die
 * Lambda ist gestorben, ohne einen Terminalstatus zu schreiben.
 *
 * Pro Task-Typ unterschiedlich, weil die Lambdas unterschiedlich lange laufen
 * dürfen. Ein gemeinsamer Wert von 18 Minuten wäre für Sync und Bestand grob
 * falsch: deren Lambdas sind nach 5 Minuten hart beendet, ihre Zeilen würden also
 * 13 Minuten länger als nötig die Oberfläche blockieren.
 */
const STALE_MINUTES_BY_TASK_TYPE: Record<TaskType, number> = {
  // 15 Minuten Laufzeit, Wächter terminalisiert bei 14:50, plus Puffer.
  Calc: 18,
  // 5 Minuten Laufzeit.
  Sync: 8,
  Stock: 8,
};

/**
 * Eine Aufgabe, die so lange auf "Pending" steht, wurde vom DynamoDB-Stream nie
 * angenommen - Stream oder Funktion sind defekt.
 */
const STUCK_PENDING_MINUTES = 3;

const TERMINAL_STATUS: TaskStatus[] = [
  "Completed",
  "Failed",
  "Timeout",
  "Stopped",
  "Skipped",
  // Der Vorgänger ist fertig; die Folgeaufgabe erscheint als eigene Zeile.
  "Continued",
];

export interface RunningTask {
  taskID: string;
  taskType: TaskType;
  status: TaskStatus;
  expansionID: string;
  startTime: string;
  stopRequestedAt: string | null;
  /** Zeile steht auf Running, ist aber zu alt - keine Lambda pollt mehr. */
  isStale: boolean;
  /** Zeile wurde nie angenommen - Stream oder Funktion defekt. */
  isStuckPending: boolean;
}

/**
 * Der laufende Aufgabenzustand, abgeleitet aus den Aufgabenzeilen selbst.
 *
 * Nicht mehr aus GlobalSettings.runningTaskID plus 15-Minuten-Wanduhr: die Sperre
 * ist seit der Verlagerung des Erwerbs in die Lambda reine Backend-Angelegenheit
 * und wird erst gesetzt, wenn die Lambda anläuft. Die Oberfläche würde den Start
 * also zu spät sehen und ein Doppelstart wäre möglich.
 *
 * "Pending" zählt bewusst als laufend: sonst gehen die Start-Buttons unmittelbar
 * nach dem Klick wieder auf.
 */
export const useRunningTask = (currentExpansionID?: string) => {
  const { calcTaskList, isLoading: isLoadingCalc } = useCalcTaskRunningList();
  const { syncTaskList, isLoading: isLoadingSync } = useSyncTaskRunningList();
  const { stockTaskList, isLoading: isLoadingStock } =
    useStockTaskRunningList();

  const isLoading = isLoadingCalc || isLoadingSync || isLoadingStock;

  const runningTask = useMemo<RunningTask | null>(() => {
    const candidates: RunningTask[] = [
      ...calcTaskList.map((task) => toRunningTask(task, "Calc")),
      ...syncTaskList.map((task) => toRunningTask(task, "Sync")),
      ...stockTaskList.map((task) => toRunningTask(task, "Stock")),
    ].filter((task): task is RunningTask => task !== null);

    if (candidates.length === 0) {
      return null;
    }

    // Bei mehreren offenen Zeilen bestimmt die jüngste die Anzeige, und der
    // Stop-Button wirkt genau auf diese. Ältere sind mit hoher Wahrscheinlichkeit
    // Reste, die über isStale ohnehin als freigebbar markiert sind.
    return candidates.reduce((newest, current) =>
      moment(current.startTime).isAfter(moment(newest.startTime))
        ? current
        : newest,
    );
  }, [calcTaskList, syncTaskList, stockTaskList]);

  /**
   * Solange die Aufgabenlisten noch geladen werden, gilt bewusst "läuft" - fail
   * closed. Andernfalls wären die Start-Buttons im ersten Frame kurz aktiv.
   */
  const isTaskRunning = Boolean(
    isLoading || (runningTask && !runningTask.isStale),
  );

  const isForeignExpansion = Boolean(
    runningTask &&
      currentExpansionID &&
      runningTask.expansionID !== currentExpansionID,
  );

  return {
    isTaskRunning,
    runningTask,
    isForeignExpansion,
    isLoading,
  };
};

interface TaskRow {
  id: string;
  status: TaskStatus;
  expansionID: string;
  startTime: string;
  stopRequestedAt?: string | null;
}

const toRunningTask = (
  task: TaskRow,
  taskType: TaskType,
): RunningTask | null => {
  if (TERMINAL_STATUS.includes(task.status)) {
    return null;
  }

  const ageMinutes = moment().diff(moment(task.startTime), "minutes");

  return {
    taskID: task.id,
    taskType,
    status: task.status,
    expansionID: task.expansionID,
    startTime: task.startTime,
    stopRequestedAt: task.stopRequestedAt ?? null,
    isStale:
      task.status === "Running" &&
      ageMinutes > STALE_MINUTES_BY_TASK_TYPE[taskType],
    isStuckPending:
      task.status === "Pending" && ageMinutes > STUCK_PENDING_MINUTES,
  };
};
